import React from 'react';
import { selectValByCondition } from '@home/utils/common';
import { getEchartTooltipString } from '.';
import { IBaseTooltipProps, IColumn, IPieTooltipProps, TColumnRender, TDataRecord } from './type';
import { getSystemFlag } from '../../pages/overview/_config/system';

/*
 * @Author: 李祥悦
 * @Date: 2024-07-11
 * @Description: 各图浮窗
 */

// 获取PC平台图标
const getPlatFormRender: TColumnRender = (value, row, column, icon) => {
  const customeIcon = getSystemFlag(value);
  return (
    <div className="dbd-custom-icon">
      {icon}
      {!['sum', 'special'].includes(row?._key) && (
        <span>
          <img src={customeIcon} />
        </span>
      )}
      <span>{value}</span>
    </div>
  );
};

// 基础浮窗
export const getBaseTooltip = (props: IBaseTooltipProps) => {
  const { params, options } = props;
  const {
    hasShare = true,
    noSortKeys = ['Others', 'others', '其他'],
    noShareKeys = [],
    valueNameCn = '值',
    valueNameEn = 'Value',
    valueFormatRule = 'thousandsInit',
    shareNameCn = '占比',
    shareNameEn = 'Share',
    shareFormatRule = 'percent1Decimal',
    hasSum = true,
    notIncludedInSumKeys = [],
    sumNameCn = '汇总',
    sumNameEn = 'Sum',
    isEn = true,
    isPlatform,
    nameRender,
    isHideNull,
    columns: propColumns = [],
    isMergeColumns,
    title,
    hideHeader,
    formatFirstThText,
    showShareLessValue = {}
  } = options || {};

  // 初步定义为头部为：名称、值、占比
  // nameRender 优先级高于 isPlatform
  const resolvedNameRender = nameRender || (isPlatform ? getPlatFormRender : undefined);
  let columns: IColumn[] = [
    {
      key: 'name',
      header:
        typeof formatFirstThText === 'function'
          ? formatFirstThText(params?.[0]?.name)
          : params?.[0]?.name,
      showIcon: true,
      render: resolvedNameRender,
    },
    {
      key: 'value',
      header: selectValByCondition(isEn, valueNameEn, valueNameCn),
      dataFormatRule: valueFormatRule,
      align: 'right',
    },
  ];

  let total = 0;

  // 过滤total
  const newParams = params?.filter((item) => !['Total', 'total'].includes(item.seriesName));

  total = newParams?.reduce((t, c) => {
    // 不统计总数的数据
    if (
      notIncludedInSumKeys?.length > 0 &&
      notIncludedInSumKeys?.includes(c.seriesName)
    ) {
      return t;
    }
    return (t += Number(c.value || 0));
  }, 0);

  // 统计占比
  if (hasShare) {
    columns.push({
      key: 'share',
      header: selectValByCondition(isEn, shareNameEn, shareNameCn),
      showShareLessValue,
      dataFormatRule: shareFormatRule,
      align: 'right',
      width: 50,
    });
  }

  // 总数
  let sumData: any[] = [];
  if (hasSum) {
    sumData = [
      {
        _key: 'sum',
        name: selectValByCondition(isEn, sumNameEn, sumNameCn),
        value: total === 0 ? '-' : total,
        share: '-',
      },
    ];
  }

  // 转换数据
  let data = newParams?.map((item) => {
    const obj: TDataRecord = {
      name: item.seriesName,
      color: item.color,
      value: item.value,
      iconType: item.iconType,
      orgData: item
    };
    if (hasShare) {
      if (noShareKeys?.includes(item.seriesName)) {
        obj.share = '';
      } else {
        obj.share = item.value === '' ? item.value : Number(item.value) / total;
      }
     
    }
    return obj;
  });
  // 不计入排序的特殊数据
  const specialData: TDataRecord[] = [];
  if (noSortKeys?.length > 0) {
    const noSpecialData: TDataRecord[] = [];
    data?.forEach((item) => {
      if (noSortKeys?.includes(item.name)) {
        specialData.push({ ...item, _key: 'special' });
      } else {
        noSpecialData.push(item);
      }
    });
    // 需要剔除特殊数据，避免排序影响
    data = noSpecialData;
  }

  // 自定义columns
  if (propColumns?.length > 0) {
    // 是否合并原有columns
    if (isMergeColumns) {
      columns = columns.map((item) => {
        // 根据key寻找外部传入的column，替换原来的
        const mergeColumn = propColumns?.find((col) => col?.key === item.key);
        if (mergeColumn) {
          return { ...item, ...mergeColumn };
        }
        return item;
      });
    } else {
      columns = propColumns;
    }
  }

  return getEchartTooltipString({
    table: {
      title,
      hideHeader,
      isHideNull,
      columns,
      data,
      sortBy: 'value',
      beforeData: sumData,
      afterData: specialData,
    },
  });
};

// 自定义value列的数据render
export const getCustomValueColumnTooltip = (props: IBaseTooltipProps, valueRender: TColumnRender) => {
  const { params, options = {} } = props || {};
  const columns = [
    {
      key: 'value',
      render: valueRender
    },
  ];

  return getBaseTooltip({ params, options: { ...options, columns, isMergeColumns: true } });
};

// 饼图浮窗
export const getPieTooltip = (props: IPieTooltipProps) => {
  const { param, options = {} } = props || {};
  const {
    title,
    valueFormatRule = 'thousandsInit',
    shareFormatRule = 'percent1Decimal',
    isPlatform,
    notIconKey = ['Others', 'others', '其他']
  } = options || {};

  // 初步定义为头部为：名称、值、占比
  const columns: IColumn[] = [
    {
      key: 'name',
      header: '',
      showIcon: true,
      render: isPlatform ? getPlatFormRender : undefined,
    },
    {
      key: 'value',
      header: '',
      dataFormatRule: valueFormatRule,
      align: 'right',
    },
    {
      key: 'share',
      header: '',
      dataFormatRule: shareFormatRule,
      align: 'right',
    },
  ];

  const { name, color, value, iconType, percent } = param;

  const data = [{
    _key: notIconKey?.includes(name) ? 'special' : undefined,
    name,
    color,
    value,
    iconType,
    share: percent / 100,
    orgData: param,
  }];

  return getEchartTooltipString({
    table: {
      title,
      hideHeader: true,
      columns,
      data,
    },
  });
};
